/**
 * excelImportHelper.ts
 *
 * Parses a multi-sheet Excel file (Assets sheet + Messages sheet) produced by
 * excelHelperMultiSheet.ts and converts the rows into the asset-node JSON
 * structure used by the FlowRenderer.
 *
 * Asset node structure (matches FlowRenderer payload):
 * {
 *   id: string,
 *   type: 'assetNode',
 *   data: {
 *     name: string,          // from asset_name column
 *     ip: string,            // from asset_ip column
 *     enabled: boolean,      // asset_status !== 'inactive'
 *     options: [             // one entry per unique component_name on this asset row
 *       {
 *         id: string,
 *         component_name: string,
 *         label: string,
 *         enabled: boolean,  // component_status !== 'inactive'
 *         data: {            // all extra dynamic columns + messages array
 *           scan_root: ...,
 *           messages: [
 *             {
 *               id: string,
 *               name: string,
 *               type: 'trigger' | 'periodic',
 *               trigger?: string,
 *               comparison?: string,
 *               value?: string,
 *               refresh_interval?: string,
 *               props: [ { id, key, uom } ]
 *             }
 *           ]
 *         }
 *       }
 *     ]
 *   }
 * }
 */

import * as ExcelJS from 'exceljs';
import * as uuid from 'uuid';
import {
  detectHeader,
  normalizeHeader,
  readDataRows,
  statusToEnabled,
  toStr,
} from './excelImportCommon';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ImportedProp = {
  id: string;
  key: string;
  uom: string;
};

export type ImportedMessage = {
  id: string;
  name: string;
  type: string;
  trigger?: string;
  comparison?: string;
  value?: string;
  refresh_interval?: string;
  props: ImportedProp[];
  [key: string]: any;
};

export type ImportedComponent = {
  id: string;
  component_name: string;
  label: string;
  enabled: boolean;
  data: Record<string, any>;
};

export type ImportedAsset = {
  id: string;
  type: 'assetNode';
  data: {
    name: string;
    ip: string;
    enabled: boolean;
    options: ImportedComponent[];
  };
};

export type ImportResult = {
  assets: ImportedAsset[];
  errors: string[];
};

export type ParseExcelOptions = {
  /** Name of the sheet containing asset rows. Defaults to 'Assets'. */
  assetsSheetName?: string;
  /** Name of the sheet containing message rows. Defaults to 'Messages'. */
  messagesSheetName?: string;
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
const coerceValue = (val: any, fieldKey?: string): any => {
  if (fieldKey === 'is' || String(val).toLowerCase() === 'true' || String(val).toLowerCase() === 'false') {
    return String(val).toLowerCase() === 'true' ? 'True' : 'False';
  }
  return val;
};

/** Columns that belong to the static asset header — not forwarded to component.data */
const ASSET_STATIC_KEYS = new Set([
  'asset_name',
  'asset_ip',
  'asset_status',
  'component_name',
  'component_status',
  'component_type',
  'component_description',
]);

/** Columns that are skipped when building the message object */
const MESSAGE_META_KEYS = new Set([
  'asset_name',
  'message_name',
  'message_type',
  'guidance',
  'status',
]);

/** Conditional payload fields resolved per message_type */
const MESSAGE_TRIGGER_KEYS = ['trigger', 'comparison', 'value'];
const MESSAGE_PERIODIC_KEYS = ['refresh_interval'];

// ---------------------------------------------------------------------------
// Message builder
// ---------------------------------------------------------------------------

const buildMessage = (row: Record<string, string>): ImportedMessage => {
  const rawMessageType = normalizeHeader(row['message_type'] ?? '');
  const msgType =
    {
      periodic: 'periodic',
      trigger: 'trigger',
      poll_periodically: 'periodic',
      subscribe_to_change: 'trigger',
    }[rawMessageType] ?? rawMessageType;
  const messageName = (row['message_name'] ?? '').trim();

  // Collect param_N_id/key/uom
  const props: ImportedProp[] = [];
  for (let n = 1; n <= 50; n++) {
    const id = String(row[`param_${n}_id`] ?? '').trim();
    const key = String(row[`param_${n}_key`] ?? '').trim();
    const uom = String(row[`param_${n}_uom`] ?? '').trim();
    if (!id && !key) break;
    props.push({ id, key, uom });
  }

  const message: ImportedMessage = {
    // In FlowRenderer UI schema, Message name is bound to messages[].id.
    // Keep id human-readable from Excel and only fall back to UUID if blank.
    id: messageName || uuid.v4(),
    name: messageName,
    type: msgType,
    props,
  };

  if (msgType === 'trigger') {
    if (row['trigger']) message.trigger = row['trigger'];
    if (row['comparison']) message.comparison = row['comparison'];
    if (row['value']) message.value = coerceValue(row['value']);
  } else if (msgType === 'periodic') {
    if (row['refresh_interval']) message.refresh_interval = row['refresh_interval'];
  } else {
    // Unknown / fallback — include everything present
    [...MESSAGE_TRIGGER_KEYS, ...MESSAGE_PERIODIC_KEYS].forEach((k) => {
      if (row[k]) message[k] = row[k];
    });
  }

  // Forward any unrecognised extra columns
  // for (const [col, val] of Object.entries(row)) {
  //   if (MESSAGE_META_KEYS.has(col)) continue;
  //   if (col.startsWith('param_')) continue;
  //   if ([...MESSAGE_TRIGGER_KEYS, ...MESSAGE_PERIODIC_KEYS].includes(col)) continue;
  //   if (col in message) continue; // already set
  //   if (val) message[col] = val;
  // }

  for (const [col, val] of Object.entries(row)) {
  if (MESSAGE_META_KEYS.has(col)) continue;
  if (col.startsWith('param_')) continue;
  if ([...MESSAGE_TRIGGER_KEYS, ...MESSAGE_PERIODIC_KEYS].includes(col)) continue;
  if (col in message) continue;
  if (val) message[col] = coerceValue(val, col);  // Type coercion
}

  return message;
};

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Parse a multi-sheet Excel workbook (ArrayBuffer) and return an array of
 * ImportedAsset objects that match the FlowRenderer asset-node payload.
 *
 * @param fileBuffer  ArrayBuffer from FileReader or fetch
 * @param options     Optional sheet name overrides
 */
export const parseExcelToAssets = async (
  fileBuffer: ArrayBuffer,
  options: ParseExcelOptions = {}
): Promise<ImportResult> => {
  const errors: string[] = [];
  const assets: ImportedAsset[] = [];
  const normalizeKey = (value: string) => value.trim().toLowerCase();
  const makeCompositeAssetKey = (assetName: string, assetIp: string) => `${normalizeKey(assetName)}::${normalizeKey(assetIp)}`;

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(fileBuffer);

  const assetsSheetName = options.assetsSheetName ?? 'Assets';
  const messagesSheetName = options.messagesSheetName ?? 'Messages';

  // Resolve sheets — fall back to first/second sheet if name not found
  const assetsSheet =
    workbook.getWorksheet(assetsSheetName) ?? workbook.worksheets[0];
  const messagesSheet =
    workbook.getWorksheet(messagesSheetName) ?? workbook.worksheets[1];

  if (!assetsSheet) {
    errors.push('No Assets sheet found in the workbook.');
    return { assets, errors };
  }

  // -------------------------------------------------------------------------
  // Parse Assets sheet
  // -------------------------------------------------------------------------
  const assetHeader = detectHeader(assetsSheet, ['asset_name']);
  if (!assetHeader) {
    errors.push(
      `Could not detect header row in sheet "${assetsSheet.name}". ` +
        'Expected a row containing "asset_name".'
    );
    return { assets, errors };
  }

  const assetRows = readDataRows(assetsSheet, assetHeader.rowNumber, assetHeader.keys, 2);

  // -------------------------------------------------------------------------
  // Parse Messages sheet (optional)
  // -------------------------------------------------------------------------
  const messagesByAsset: Record<string, ImportedMessage[]> = {};

  if (messagesSheet) {
    const msgHeader = detectHeader(messagesSheet, ['message_name']);
    if (!msgHeader) {
      errors.push(
        `Could not detect header row in sheet "${messagesSheet.name}". Messages will be skipped.`
      );
    } else {
      const messageRawRows = readDataRows(messagesSheet, msgHeader.rowNumber, msgHeader.keys, 2);

      for (const row of messageRawRows) {
        const msgType = row['message_type'];
        if (!msgType) continue; // skip rows without a message type

        const assetName = row['asset_name'] ?? '';
        const assetIp = row['asset_ip'] ?? '';
        const compositeKey = makeCompositeAssetKey(assetName, assetIp);
        if (!messagesByAsset[compositeKey]) messagesByAsset[compositeKey] = [];
        messagesByAsset[compositeKey].push(buildMessage(row));
      }
    }
  }

  // -------------------------------------------------------------------------
  // Build asset nodes (group rows by asset so one asset can hold many components)
  // -------------------------------------------------------------------------
  const assetsByName = new Map<string, ImportedAsset>();

  for (const row of assetRows) {
    const assetName = row['asset_name'] ?? '';
    const assetIp = row['asset_ip'] ?? '';

    if (!assetName && !assetIp) continue; // fully empty row

    const assetEnabled = statusToEnabled(row['asset_status']);
    const normalizedAssetName = normalizeKey(assetName);
    const componentName = row['component_name'] ?? '';
    const normalizedComponentName = normalizeKey(componentName);
    const componentEnabled = statusToEnabled(row['component_status']);

    // Dynamic component config fields (everything that is not a known asset column)
    const componentData: Record<string, any> = {};
    for (const [key, val] of Object.entries(row)) {
      if (ASSET_STATIC_KEYS.has(key)) continue;
      if (val) componentData[key] = val;
    }

    // Attach messages belonging to this asset
    const messages = messagesByAsset[makeCompositeAssetKey(assetName, assetIp)] ?? messagesByAsset[makeCompositeAssetKey(assetName, '')] ?? [];
    if (messages.length > 0) {
      componentData.messages = messages;
    }

    const options: ImportedComponent[] = [];
    if (componentName) {
      options.push({
        id: uuid.v4(),
        component_name: componentName,
        label: componentName,
        enabled: componentEnabled,
        data: componentData,
      });
    }

    // Group imported rows by asset name only.
    const byName = normalizedAssetName ? assetsByName.get(normalizedAssetName) : undefined;
    let existingAsset: ImportedAsset | undefined = byName;

    if (!existingAsset) {
      existingAsset = {
        id: uuid.v4(),
        type: 'assetNode',
        data: {
          name: assetName,
          ip: assetIp,
          enabled: assetEnabled,
          options: [],
        },
      };
      assets.push(existingAsset);
    } else {
      // Backfill empty display values when future rows provide them.
      if (!existingAsset.data.name && assetName) existingAsset.data.name = assetName;
      if (!existingAsset.data.ip && assetIp) existingAsset.data.ip = assetIp;
    }

    if (normalizedAssetName) assetsByName.set(normalizedAssetName, existingAsset);

    existingAsset.data.enabled = existingAsset.data.enabled && assetEnabled;

    if (options.length > 0) {
      const incomingComponent = options[0];
      const existingComponentIndex = existingAsset.data.options.findIndex(
        (opt) => normalizeKey(opt.component_name) === normalizedComponentName
      );

      if (existingComponentIndex >= 0) {
        errors.push(
          `Duplicate component "${componentName}" found for asset "${assetName || assetIp}". Keeping the first entry and skipping duplicates.`
        );
      } else {
        existingAsset.data.options.push(incomingComponent);
      }
    }
  }

  // Warn about messages that reference an asset not in the Assets sheet
  for (const compositeKey of Object.keys(messagesByAsset)) {
    const [messageAssetName, messageAssetIp] = compositeKey.split('::');
    const found = assets.find(
      (a) =>
        normalizeKey(a.data.name || '') === (messageAssetName || '') &&
        normalizeKey(a.data.ip || '') === (messageAssetIp || '')
    );
    if (!found) {
      errors.push(
        `Asset "${messageAssetName || messageAssetIp}" is referenced in the Messages sheet but was not found in the Assets sheet.`
      );
    }
  }

  return { assets, errors };
};